import React from "react";
import { Link } from "react-router-dom";
import { ManageCookiesLink } from "../components/CookieConsent";
import { ArrowLeft } from "lucide-react";
import { Logo } from "../components/Logo";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-[var(--gt-bg-marketing)]" data-testid="privacy-page">
      <nav className="gt-glass-nav">
        <div className="max-w-3xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <Logo size="md" />
          <Link to="/" className="gt-btn-ghost text-sm" data-testid="privacy-home-link">
            <ArrowLeft size={14} /> Home
          </Link>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-6 sm:px-10 py-12 sm:py-16 prose-invert-alt">
        <div className="gt-label mb-3">Privacy notice</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter leading-[1.05] mb-4">
          Your privacy, in plain Australian English.
        </h1>
        <p className="text-[var(--gt-ink-2)] text-sm mb-10">
          Effective from launch · Aligned to the <strong>Australian Privacy Principles (APPs)</strong> under the <em>Privacy Act 1988 (Cth)</em>.
        </p>

        <Section title="1. What we collect">
          <p>Only what we actually need to sell and deliver tickets:</p>
          <ul>
            <li><strong>Buyers</strong>: name, email, ticket purchase details, IP + user-agent (for security &amp; fraud only).</li>
            <li><strong>Organisers</strong>: name, email, business name, state, payout details (BSB/account for future payouts).</li>
            <li><strong>Never</strong>: card numbers, CVV, or expiry. All payment data is handled directly by <strong>Stripe (PCI-DSS Level 1)</strong>.</li>
          </ul>
        </Section>

        <Section title="2. Why we collect it">
          <p>To issue tickets, process payments, contact you about your order, prevent fraud, and produce compliance reports (GST etc.). We do not sell your data. We do not use it for third-party advertising.</p>
        </Section>

        <Section title="3. Who sees it">
          <ul>
            <li>The <strong>event organiser</strong> — sees the buyer name, email, ticket type &amp; check-in status for their own event only. Never other organisers&apos; data.</li>
            <li><strong>Stripe</strong> — processes payments securely; we never store your card details.</li>
            <li><strong>Emergent hosting</strong> — infrastructure &amp; object storage for event banners.</li>
            <li><strong>Getickets internal admin</strong> — only for support, dispute or policy enforcement. Every admin action is audit-logged.</li>
          </ul>
        </Section>

        <Section title="4. How long we keep it">
          <p>Order &amp; refund records are kept for at least <strong>7 years</strong> to meet ATO record-keeping obligations. You can request access or correction at any time (APP 12/13).</p>
        </Section>

        <Section title="5. Security">
          <ul>
            <li>HTTPS enforced end-to-end (HSTS).</li>
            <li>Passwords hashed with bcrypt (cost 12).</li>
            <li>JWT auth in httpOnly cookies.</li>
            <li>Rate-limited login / signup / password-reset / scanner / checkout.</li>
            <li>Every admin &amp; refund action recorded to an immutable audit log.</li>
          </ul>
        </Section>

        <Section title="6. Your rights">
          <p>Contact <a href="mailto:privacy@getickets.com.au" className="underline">privacy@getickets.com.au</a> to access, correct, or delete your personal information. You can also complain to the <a href="https://www.oaic.gov.au" target="_blank" rel="noreferrer" className="underline">OAIC</a> if you believe we&apos;ve mishandled your data.</p>
        </Section>

        <Section title="7. Changes">
          <p>We&apos;ll post any material changes here and notify existing account holders by email.</p>
        </Section>
      </article>

      <footer className="bg-[var(--gt-ink)] text-[var(--gt-bg-marketing)] mt-12">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <Logo inverse size="md" />
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-5">
            <ManageCookiesLink className="text-xs tracking-[0.25em] uppercase text-[var(--gt-ink-3)]" />
            <div className="text-xs tracking-[0.25em] uppercase text-[var(--gt-ink-3)]">
              © {new Date().getFullYear()} Getickets · AU
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-8" data-testid={`privacy-section-${title.split(".")[0].trim()}`}>
      <h2 className="font-heading text-2xl font-black tracking-tighter mb-3">{title}</h2>
      <div className="text-sm text-[var(--gt-ink-2)] leading-relaxed space-y-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul_li]:mb-1">
        {children}
      </div>
    </section>
  );
}
